import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AnimatePresence, motion } from 'framer-motion'
import { CalendarDays, FileSearch, FileText, ListFilter, LoaderCircle, MessageSquare, Search, Upload, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { postMemoryQuery } from './memoryQuery'
import type { MemoryBankPayload, MemoryCopy, MemoryDocumentDetail, MemoryDocumentFilter, MemoryDocumentsPayload, MemoryDocumentSummary, MemoryGraphPayload } from './types'
import { displayDocumentName, documentPreview, formatMemoryDate } from './utils'

interface Props {
  workerId: string
  copy: MemoryCopy
  canManage: boolean
  refreshKey: number
  resetKey: number
  onSelect: (document: MemoryDocumentSummary) => void
  onPreview: (document: MemoryDocumentSummary, focusedGraph?: MemoryGraphPayload, isolate?: boolean) => void
  onSearchResults: (documents: MemoryDocumentSummary[], query: string, graph?: MemoryGraphPayload) => void
  onClearFocus: () => void
  onAddDocument: () => void
}

const PAGE_SIZE = 25
const MIN_QUERY_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 800
const UPLOAD_POLL_MS = 5_000
const DOCUMENT_FILTERS: MemoryDocumentFilter[] = ['all', 'conversations', 'files']

export default function MemoryDocumentsMode({ workerId, copy, canManage, refreshKey, resetKey, onSelect, onPreview, onSearchResults, onClearFocus, onAddDocument }: Props) {
  const [query, setQuery] = useState('')
  const [documents, setDocuments] = useState<MemoryDocumentSummary[]>([])
  const [previews, setPreviews] = useState<Record<string, MemoryDocumentDetail>>({})
  const [category, setCategory] = useState<MemoryDocumentFilter>('all')
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const listRequestRef = useRef<AbortController | null>(null)
  const previewRequestsRef = useRef(new Map<string, AbortController>())
  const graphRequestsRef = useRef(new Map<string, AbortController>())
  const graphCacheRef = useRef(new Map<string, MemoryGraphPayload>())
  const activeDocumentRef = useRef('')
  const previewsRef = useRef(previews)
  const documentsRef = useRef(documents)
  const previousResetRef = useRef(resetKey)
  const lastDebouncedQueryRef = useRef('')

  useEffect(() => { previewsRef.current = previews }, [previews])
  useEffect(() => { documentsRef.current = documents }, [documents])

  const loadDocuments = useCallback(async (offset: number, append: boolean, nextQuery: string, nextCategory: MemoryDocumentFilter, preserveLoaded = false) => {
    const normalizedQuery = nextQuery.trim()
    const effectiveQuery = normalizedQuery.length >= MIN_QUERY_LENGTH ? normalizedQuery : ''
    const clearFailedRefresh = () => {
      if (append || preserveLoaded) return

      documentsRef.current = []
      setDocuments([])
      setTotal(0)
      onSearchResults([], effectiveQuery)
    }
    listRequestRef.current?.abort()
    const controller = new AbortController()
    listRequestRef.current = controller
    setLoading(true)
    setError(null)
    try {
      const result = await postMemoryQuery<MemoryDocumentsPayload>(
        `/dashboard/digital_workers/${workerId}/memory_documents_search`,
        { query: effectiveQuery, category: nextCategory, limit: PAGE_SIZE, offset },
        controller.signal,
      )
      if (!result.ok) {
        setError(result.data.status === 'unavailable' ? copy.documents.unavailable : copy.documents.error)
        clearFailedRefresh()
        return
      }
      const nextDocuments = result.data.documents || []
      const nextTotal = result.data.total || 0
      const partialRefresh = preserveLoaded && result.data.partial
      const visibleDocuments = append
        ? [...documentsRef.current, ...nextDocuments]
        : preserveLoaded
          ? ([
              ...nextDocuments,
              ...documentsRef.current.filter((document) => !nextDocuments.some((refreshed) => refreshed.id === document.id)),
            ]).slice(0, partialRefresh ? undefined : Math.min(Math.max(documentsRef.current.length, nextDocuments.length), nextTotal))
          : nextDocuments
      documentsRef.current = visibleDocuments
      setDocuments(visibleDocuments)
      setTotal((current) => partialRefresh ? Math.max(current, nextTotal, visibleDocuments.length) : nextTotal)
      if (!preserveLoaded) onSearchResults(visibleDocuments, effectiveQuery, result.data.graph)
    } catch (requestError) {
      if ((requestError as Error).name !== 'AbortError') {
        setError(copy.documents.error)
        clearFailedRefresh()
      }
    } finally {
      if (listRequestRef.current === controller) setLoading(false)
    }
  }, [copy.documents.error, copy.documents.unavailable, onSearchResults, workerId])

  const clearSearch = useCallback(() => {
    listRequestRef.current?.abort()
    lastDebouncedQueryRef.current = ''
    setQuery('')
    onSearchResults([], '')
    void loadDocuments(0, false, '', category)
  }, [category, loadDocuments, onSearchResults])

  const loadPreview = useCallback(async (document: MemoryDocumentSummary) => {
    if (previewsRef.current[document.id] || previewRequestsRef.current.has(document.id)) return
    const controller = new AbortController()
    previewRequestsRef.current.set(document.id, controller)
    try {
      const result = await postMemoryQuery<MemoryDocumentsPayload>(
        `/dashboard/digital_workers/${workerId}/memory_documents_search`,
        { document_id: document.id, preview: true },
        controller.signal,
      )
      if (result.ok && result.data.document) setPreviews((current) => ({ ...current, [document.id]: result.data.document as MemoryDocumentDetail }))
      else setPreviews((current) => ({ ...current, [document.id]: emptyPreview(document) }))
    } catch (requestError) {
      if ((requestError as Error).name !== 'AbortError') setPreviews((current) => ({ ...current, [document.id]: emptyPreview(document) }))
    } finally {
      previewRequestsRef.current.delete(document.id)
    }
  }, [workerId])

  const focusDocument = useCallback(async (document: MemoryDocumentSummary) => {
    activeDocumentRef.current = document.id
    graphRequestsRef.current.forEach((controller, documentId) => {
      if (documentId === document.id) return
      controller.abort()
      graphRequestsRef.current.delete(documentId)
    })
    const hasEffectiveQuery = query.trim().length >= MIN_QUERY_LENGTH
    if (document.category !== 'files' || hasEffectiveQuery) {
      onPreview(document)
      return
    }

    const cachedGraph = graphCacheRef.current.get(document.id)
    onPreview(document, cachedGraph, true)
    if (cachedGraph || graphRequestsRef.current.has(document.id)) return

    const controller = new AbortController()
    graphRequestsRef.current.set(document.id, controller)
    try {
      const result = await postMemoryQuery<MemoryBankPayload>(
        `/dashboard/digital_workers/${workerId}/memory_document_graph`,
        { document_id: document.id },
        controller.signal,
      )
      if (result.ok && result.data.status === 'ready') {
        graphCacheRef.current.set(document.id, result.data.graph)
        if (activeDocumentRef.current === document.id) onPreview(document, result.data.graph, true)
      }
    } catch (requestError) {
      if ((requestError as Error).name !== 'AbortError') return
    } finally {
      graphRequestsRef.current.delete(document.id)
    }
  }, [onPreview, query, workerId])

  const selectCategory = (nextCategory: MemoryDocumentFilter) => {
    const normalizedQuery = query.trim()
    lastDebouncedQueryRef.current = normalizedQuery.length >= MIN_QUERY_LENGTH ? normalizedQuery : ''
    setCategory(nextCategory)
    if (nextCategory !== 'files') onClearFocus()
    void loadDocuments(0, false, query, nextCategory)
  }

  useEffect(() => {
    const normalizedQuery = query.trim()
    const effectiveQuery = normalizedQuery.length >= MIN_QUERY_LENGTH ? normalizedQuery : ''
    if (effectiveQuery === lastDebouncedQueryRef.current) return

    const timeout = window.setTimeout(() => {
      lastDebouncedQueryRef.current = effectiveQuery
      void loadDocuments(0, false, effectiveQuery, category)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timeout)
  }, [category, loadDocuments, query])

  useEffect(() => {
    activeDocumentRef.current = ''
    graphRequestsRef.current.forEach((controller) => controller.abort())
    graphRequestsRef.current.clear()
    graphCacheRef.current.clear()
    const normalizedQuery = query.trim()
    lastDebouncedQueryRef.current = normalizedQuery.length >= MIN_QUERY_LENGTH ? normalizedQuery : ''
    void loadDocuments(0, false, query, category)
  }, [refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (previousResetRef.current === resetKey) return
    previousResetRef.current = resetKey
    listRequestRef.current?.abort()
    lastDebouncedQueryRef.current = ''
    setQuery('')
    void loadDocuments(0, false, '', category)
  }, [category, loadDocuments, resetKey])
  useEffect(() => {
    const awaitingIndex = documents.some((document) => document.upload_status === 'pending' || document.upload_status === 'processing')
    if (!awaitingIndex || loading) return

    const timeout = window.setTimeout(() => {
      void loadDocuments(0, false, query, category, true)
    }, UPLOAD_POLL_MS)
    return () => window.clearTimeout(timeout)
  }, [category, documents, loading, loadDocuments, query])
  useEffect(() => () => {
    listRequestRef.current?.abort()
    previewRequestsRef.current.forEach((controller) => controller.abort())
    graphRequestsRef.current.forEach((controller) => controller.abort())
  }, [])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border p-3">
        <div id="memory-document-search">
          <div className="min-w-0 flex-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground transition-colors" />
              <Input
                type="text"
                role="searchbox"
                aria-label={copy.documents.search_label}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={copy.documents.search_placeholder}
                minLength={MIN_QUERY_LENGTH}
                className="h-11 rounded-xl border-border/80 bg-muted/20 pl-10 pr-11 text-sm shadow-none transition-[background-color,border-color] hover:bg-muted/30 focus-visible:border-muted-foreground/45 focus-visible:bg-background focus-visible:ring-0"
              />
              <AnimatePresence initial={false}>
                {query && (
                  <motion.div className="absolute right-1.5 top-1.5" initial={{ opacity: 0, scale: 0.72 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.72 }} transition={{ duration: 0.14 }}>
                    <Button type="button" variant="ghost" size="icon-sm" onClick={clearSearch} aria-label={copy.filters.clear} className="size-8"><X /></Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            {documents.length > 0 && (
              <p className="truncate text-[10.5px] tabular-nums text-faint-foreground">
                {copy.documents.result_count.replace('%{shown}', String(documents.length)).replace('%{total}', String(total))}
              </p>
            )}
          </div>
          {loading && documents.length > 0 && <LoaderCircle className="size-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant={category === 'all' ? 'outline' : 'secondary'} size="icon-sm" aria-label={copy.documents.filter_label} className="size-8 shrink-0 rounded-lg">
                    <ListFilter />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>{copy.documents.filter_label}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="min-w-48">
              <DropdownMenuRadioGroup value={category} onValueChange={(value) => selectCategory(value as MemoryDocumentFilter)}>
                {DOCUMENT_FILTERS.map((item) => (
                  <DropdownMenuRadioItem key={item} value={item}>
                    {item === 'conversations' && <MessageSquare />}
                    {item === 'files' && <FileText />}
                    {copy.documents.filters[item]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {canManage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="outline" size="icon-sm" onClick={onAddDocument} aria-label={copy.documents.add_document} className="size-8 shrink-0 rounded-lg">
                  <Upload />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>{copy.documents.add_document}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="p-3">
          {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
          {loading && documents.length === 0 && <DocumentStatus icon={<LoaderCircle className="size-5 animate-spin text-primary" />} title={copy.documents.loading} />}
          {!loading && documents.length === 0 && !error && (
            <EmptyDocuments
              title={query.trim() ? copy.documents.empty : copy.documents.filter_empty}
              description={query.trim() ? `“${query.trim()}”` : copy.documents.description}
            />
          )}

          {documents.length > 0 && (
            <div data-testid="memory-documents-results" className="space-y-1">
              {documents.map((document) => <DocumentCard key={document.id} document={document} preview={previews[document.id]} copy={copy} onVisible={loadPreview} onPreview={focusDocument} onSelect={onSelect} />)}
            </div>
          )}
          {documents.length < total && <Button type="button" variant="outline" disabled={loading} onClick={() => void loadDocuments(documents.length, true, query, category)} className="mt-3 w-full">{loading && <LoaderCircle className="animate-spin" />}{copy.documents.load_more}</Button>}
        </div>
      </ScrollArea>
    </div>
  )
}

function EmptyDocuments({ title, description }: { title: string; description: string }) {
  return (
    <motion.div className="flex min-h-72 flex-col items-center justify-center px-6 text-center" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: 'easeOut' }}>
      <span className="relative mb-4 grid size-11 place-items-center rounded-2xl border border-primary/15 bg-primary/[0.06] text-primary shadow-sm">
        <span className="absolute inset-1 rounded-xl border border-primary/10" />
        <FileSearch className="relative size-5" />
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1.5 max-w-64 text-xs leading-5 text-muted-foreground">{description}</p>
    </motion.div>
  )
}

function DocumentStatus({ icon, title }: { icon: ReactNode; title: string }) {
  return <div className="flex min-h-52 items-center justify-center gap-2.5 text-xs text-muted-foreground">{icon}<span>{title}</span></div>
}

function emptyPreview(document: MemoryDocumentSummary): MemoryDocumentDetail {
  return { ...document, original_text: null, nodes_by_fact_type: { world: 0, experience: 0, observation: 0 }, linked_memories: [] }
}

function DocumentCard({ document, preview, copy, onVisible, onPreview, onSelect }: { document: MemoryDocumentSummary; preview?: MemoryDocumentDetail; copy: MemoryCopy; onVisible: (document: MemoryDocumentSummary) => void; onPreview: (document: MemoryDocumentSummary) => void; onSelect: (document: MemoryDocumentSummary) => void }) {
  const cardRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const element = cardRef.current
    if (!element || preview || document.upload_status) return
    const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { onVisible(document); observer.disconnect() } }, { rootMargin: '80px' })
    observer.observe(element)
    return () => observer.disconnect()
  }, [document, onVisible, preview])

  const text = document.upload_status ? copy.documents.upload_status[document.upload_status] : documentPreview(preview?.original_text)
  const date = document.event_date || document.created_at
  const awaitingIndex = document.upload_status === 'pending' || document.upload_status === 'processing'
  return (
    <button ref={cardRef} type="button" disabled={awaitingIndex} onPointerEnter={() => { if (!document.upload_status) void onPreview(document) }} onFocus={() => { if (!document.upload_status) void onPreview(document) }} onClick={() => { if (!awaitingIndex) onSelect(document) }} className="group flex w-full items-start gap-3 rounded-lg border border-transparent p-3 text-left transition-colors hover:border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-default disabled:hover:border-transparent disabled:hover:bg-transparent">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        {awaitingIndex ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-3 block text-xs font-medium leading-5 text-foreground/85">{text || (preview ? copy.documents.preview_unavailable : copy.documents.preview_loading)}</span>
        <span className="mt-2 flex min-w-0 items-center gap-2 text-[10.5px] text-faint-foreground">
          <span className="min-w-0 flex-1 truncate">{displayDocumentName(document.name)}</span>
          {date && <span className="inline-flex shrink-0 items-center gap-1 tabular-nums"><CalendarDays className="h-3 w-3" />{formatMemoryDate(date, false)}</span>}
        </span>
      </span>
    </button>
  )
}
